/**
 * Clipboard utility functions
 *
 * `copyToClipboard` is the primary path for getting a composed compliance message
 * to the user's mail client — it works in every browser, every OS, and webmail
 * included, which is why it is preferred over `mailto:` (see `mailto.ts`).
 *
 * Deliberately dependency-free plain functions: no Angular injection, no I/O beyond
 * the clipboard itself, so callers can use them anywhere and tests can stub the
 * browser globals directly.
 */

/**
 * Copy text to the system clipboard.
 *
 * Tries the async Clipboard API first, then falls back to a temporary textarea
 * plus `document.execCommand('copy')` for non-secure contexts (plain http, older
 * browsers) where `navigator.clipboard` is undefined or rejects.
 *
 * Never throws — resolves `false` on failure so the caller can show an accurate
 * toast instead of claiming a copy that did not happen.
 *
 * @param text - Text to place on the clipboard
 * @returns `true` when the text reached the clipboard, `false` otherwise
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  const value = text ?? '';

  if (await copyWithClipboardApi(value)) {
    return true;
  }

  return copyWithExecCommand(value);
}

/**
 * Preferred path: the async Clipboard API.
 * Unavailable in non-secure contexts, and can reject when the document is not focused.
 */
async function copyWithClipboardApi(text: string): Promise<boolean> {
  const nav: Navigator | undefined = globalThis.navigator;

  if (typeof nav?.clipboard?.writeText !== 'function') {
    return false;
  }

  try {
    await nav.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Fallback path: select the text inside an off-screen textarea and run the legacy
 * `execCommand('copy')`. Deprecated but still the only option on http origins.
 */
function copyWithExecCommand(text: string): boolean {
  const doc: Document | undefined = globalThis.document;

  if (!doc?.body || typeof doc.createElement !== 'function' || typeof doc.execCommand !== 'function') {
    return false;
  }

  const previouslyFocused = doc.activeElement as HTMLElement | null;
  const textarea = doc.createElement('textarea');

  textarea.value = text;
  // readonly keeps the mobile keyboard from opening; the selection still works
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  textarea.setAttribute('tabindex', '-1');
  // Positioned off-screen rather than hidden — a display:none element cannot be selected
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '-9999px';
  textarea.style.opacity = '0';

  doc.body.appendChild(textarea);

  try {
    textarea.focus();
    textarea.select();

    if (typeof textarea.setSelectionRange === 'function') {
      textarea.setSelectionRange(0, text.length);
    }

    return doc.execCommand('copy') === true;
  } catch {
    return false;
  } finally {
    removeNode(doc, textarea);

    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
  }
}

/**
 * Remove the temporary textarea without letting a teardown error mask the copy result.
 */
function removeNode(doc: Document, node: HTMLElement): void {
  try {
    if (typeof node.remove === 'function') {
      node.remove();
    } else {
      doc.body.removeChild(node);
    }
  } catch {
    // The node is already detached — nothing to clean up
  }
}
